import Required from 'components/Required';
import React, { ChangeEvent } from 'react';
import { FormGroup, Label, Input, Button, Row, Col } from 'reactstrap';
import { MatriculaRequest } from 'types/Matriculas';
import { DatePickerChangeEvent } from 'components/DatePicker';
import { Provincias } from 'types/Provincias';
import { Partidos } from 'types/Partidos';
import { Localidades } from 'types/Localidades';

interface Props {
  data: Partial<MatriculaRequest>;
  formValidation: Partial<Record<keyof MatriculaRequest, boolean>>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => void;
  isLoading: boolean;
  particularPartidos: Partidos[];
  particularLocalidades: Localidades[];
  legalPartidos: Partidos[];
  legalLocalidades: Localidades[];
  particularProvincias: Provincias[];
  onBack: () => void;
  onNext: () => void;
  validateStep: (requiredFields: string[], isForm?: boolean) => boolean;
  disableSend: boolean;
}

const StepTwo = ({
  data,
  formValidation,
  handleInputChange,
  isLoading,
  particularPartidos,
  particularLocalidades,
  legalPartidos,
  legalLocalidades,
  particularProvincias,
  onBack,
  onNext,
  validateStep,
  disableSend,
}: Props) => {
  const validateStepTwo = (): boolean => {
    const requiredFields = [
      'particular_calle',
      'particular_numero',
      'particular_provincia_id',
      'particular_partido_id',
      'particular_localidad_id',
      'particular_codigo_postal',
      'legal_calle',
      'legal_numero',
      'legal_partido_id',
      'legal_localidad_id',
      'legal_codigo_postal',
      'legal_telefono',
      'legal_celular',
    ];

    return validateStep(requiredFields);
  };

  return (
    <div style={{ padding: '0 10rem 1rem 10rem' }}>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_calle'
            >
              Calle <Required />
            </Label>
            <Input
              id='particular_calle'
              name='particular_calle'
              value={data.particular_calle ?? ''}
              invalid={formValidation.particular_calle === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_numero'
            >
              Número <Required />
            </Label>
            <Input
              id='particular_numero'
              name='particular_numero'
              value={data.particular_numero ?? ''}
              invalid={formValidation.particular_numero === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_piso'
            >
              Piso
            </Label>
            <Input
              id='particular_piso'
              name='particular_piso'
              value={data.particular_piso ?? ''}
              invalid={formValidation.particular_piso === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_dpto'
            >
              Departamento
            </Label>
            <Input
              id='particular_dpto'
              name='particular_dpto'
              value={data.particular_dpto ?? ''}
              invalid={formValidation.particular_dpto === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={2}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_prefijo_telefono'
            >
              Prefijo <Required />
            </Label>
            <Input
              id='particular_prefijo_telefono'
              name='particular_prefijo_telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.particular_prefijo_telefono ?? ''}
              invalid={formValidation.particular_prefijo_telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={4}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_telefono'
            >
              Teléfono <Required />
            </Label>
            <Input
              id='particular_telefono'
              name='particular_telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.particular_telefono ?? ''}
              invalid={formValidation.particular_telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_codigo_postal'
            >
              Código postal <Required />
            </Label>
            <Input
              id='particular_codigo_postal'
              name='particular_codigo_postal'
              value={data.particular_codigo_postal ?? ''}
              invalid={formValidation.particular_codigo_postal === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_provincia_id'
            >
              Provincia <Required />
            </Label>
            <Input
              id='particular_provincia_id'
              name='particular_provincia_id'
              type='select'
              className='custom-select form-control'
              value={data.particular_provincia_id ?? ''}
              invalid={formValidation.particular_provincia_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !particularProvincias.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {particularProvincias.map((item: any) => {
                return (
                  <option
                    key={item.MTR052_ID_PROVINCIA}
                    value={item.MTR052_ID_PROVINCIA}
                  >
                    {item.MTR052_D_PROVINCIA}
                  </option>
                );
              })}
            </Input>
          </Col>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_partido_id'
            >
              Partido <Required />
            </Label>
            <Input
              id='particular_partido_id'
              name='particular_partido_id'
              type='select'
              className='custom-select form-control'
              value={data.particular_partido_id ?? ''}
              invalid={formValidation.particular_partido_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !particularPartidos.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {particularPartidos.map((item: any) => {
                return (
                  <option
                    key={item.MTR038_ID_PARTIDO}
                    value={item.MTR038_ID_PARTIDO}
                  >
                    {item.MTR038_D_PARTIDO}
                  </option>
                );
              })}
            </Input>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='particular_localidad_id'
            >
              Localidad <Required />
            </Label>
            <Input
              id='particular_localidad_id'
              name='particular_localidad_id'
              type='select'
              className='custom-select form-control'
              value={data.particular_localidad_id ?? ''}
              invalid={formValidation.particular_localidad_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !particularLocalidades.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {particularLocalidades.map((item: any) => {
                return (
                  <option
                    key={item.MTR008_ID_LOCALIDAD}
                    value={item.MTR008_ID_LOCALIDAD}
                  >
                    {item.MTR008_D_LOCALIDAD}
                  </option>
                );
              })}
            </Input>
          </Col>
          <Col sm={6}></Col>
        </Row>
      </FormGroup>
      <hr className='my-4' />
      <Col sm={12} className='text-center mb-3' style={{ marginBottom: '1.5rem' }}>
        <span
          style={{
            fontSize: '1rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Dirección legal
        </span>
        <p>Sólo se podrá constituir domicilio legal en la Provincia de Buenos Aires.</p>
      </Col>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_calle'
            >
              Calle <Required />
            </Label>
            <Input
              id='legal_calle'
              name='legal_calle'
              value={data.legal_calle ?? ''}
              invalid={formValidation.legal_calle === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_numero'
            >
              Número <Required />
            </Label>
            <Input
              id='legal_numero'
              name='legal_numero'
              value={data.legal_numero ?? ''}
              invalid={formValidation.legal_numero === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_piso'
            >
              Piso
            </Label>
            <Input
              id='legal_piso'
              name='legal_piso'
              value={data.legal_piso ?? ''}
              invalid={formValidation.legal_piso === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_dpto'
            >
              Departamento
            </Label>
            <Input
              id='legal_dpto'
              name='legal_dpto'
              value={data.legal_dpto ?? ''}
              invalid={formValidation.legal_dpto === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={2}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_prefijo_telefono'
            >
              Prefijo <Required />
            </Label>
            <Input
              id='legal_prefijo_telefono'
              name='legal_prefijo_telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.legal_prefijo_telefono ?? ''}
              invalid={formValidation.legal_prefijo_telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={4}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_telefono'
            >
              Teléfono <Required />
            </Label>
            <Input
              id='legal_telefono'
              name='legal_telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.legal_telefono ?? ''}
              invalid={formValidation.legal_telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_codigo_postal'
            >
              Código postal <Required />
            </Label>
            <Input
              id='legal_codigo_postal'
              name='legal_codigo_postal'
              value={data.legal_codigo_postal ?? ''}
              invalid={formValidation.legal_codigo_postal === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_partido_id'
            >
              Partido <Required />
            </Label>
            <Input
              id='legal_partido_id'
              name='legal_partido_id'
              type='select'
              className='custom-select form-control'
              value={data.legal_partido_id ?? ''}
              invalid={formValidation.legal_partido_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !legalPartidos.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {legalPartidos.map((item: any) => {
                return (
                  <option
                    key={item.MTR038_ID_PARTIDO}
                    value={item.MTR038_ID_PARTIDO}
                  >
                    {item.MTR038_D_PARTIDO}
                  </option>
                );
              })}
            </Input>
          </Col>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='legal_localidad_id'
            >
              Localidad <Required />
            </Label>
            <Input
              id='legal_localidad_id'
              name='legal_localidad_id'
              type='select'
              className='custom-select form-control'
              value={data.legal_localidad_id ?? ''}
              invalid={formValidation.legal_localidad_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend || !legalLocalidades.length}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {legalLocalidades.map((item: any) => {
                return (
                  <option
                    key={item.MTR008_ID_LOCALIDAD}
                    value={item.MTR008_ID_LOCALIDAD}
                  >
                    {item.MTR008_D_LOCALIDAD}
                  </option>
                );
              })}
            </Input>
          </Col>
        </Row>
      </FormGroup>
      <div style={{ marginTop: 30 }} className='d-flex justify-content-center'>
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={ onBack }
        >
          Anterior
        </Button>
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={() => {
            if (validateStepTwo()) onNext();
          }}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default StepTwo;
